/**
 * Redemptions Page - Verify and Track Coupon Usage
 * 
 * Allows Tammy to:
 * - Validate coupon codes before accepting
 * - Record redemptions with customer info
 * - View redemption history
 * - Check for fraud (repeat customers)
 * - Void incorrect redemptions
 */

import { useState, useEffect } from 'react';
import { 
  HiSearch,
  HiCheck,
  HiX,
  HiRefresh,
  HiExclamation,
  HiClipboardCheck,
  HiUser,
  HiClock,
  HiBan
} from 'react-icons/hi';
import { useAuth } from '../hooks/useAuth';
import { adminFetch, adminHeaders } from '../lib/adminApi';

// Types
interface PromoValidation {
  valid: boolean;
  message: string;
  promo: {
    id: string;
    title: string;
    description: string;
    discount_type: string;
    discount_value: number | null;
    free_item: string | null;
    target_audience: string | null;
    uses_remaining: number | null;
    requires_verification: boolean;
  } | null;
}

interface Redemption {
  id: string;
  promo_code: string;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  redeemed_at: string;
  redeemed_by: string;
  status: 'redeemed' | 'voided';
  verification_method: string;
  notes: string | null;
  promo: {
    title: string;
    promo_code: string;
    discount_type: string;
    discount_value: number | null;
    free_item: string | null;
  };
}

interface Stats {
  total_redemptions: number;
  today_redemptions: number;
  week_redemptions: number;
  top_promos: Array<{
    id: string;
    title: string;
    promo_code: string;
    current_uses: number;
    max_total_uses: number | null;
  }>;
}

export default function Redemptions() {
  const { user } = useAuth();
  // State
  const [code, setCode] = useState('');
  const [validation, setValidation] = useState<PromoValidation | null>(null);
  const [validating, setValidating] = useState(false);
  
  // Redemption form
  const [showRedeemForm, setShowRedeemForm] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  
  // History
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  
  // Success message
  const [successMessage, setSuccessMessage] = useState('');

  // Load redemption history and stats
  useEffect(() => {
    fetchHistory();
    fetchStats();
  }, []);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await adminFetch('/api/redemptions?limit=50');
      if (response.ok) {
        const data = await response.json();
        setRedemptions(data.redemptions || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminFetch('/api/redemptions/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Validate promo code
  const validateCode = async () => {
    if (!code.trim()) return;
    
    setValidating(true);
    setValidation(null);
    setShowRedeemForm(false);
    
    try {
      const params = new URLSearchParams();
      if (customerEmail) params.append('email', customerEmail);
      if (customerPhone) params.append('phone', customerPhone);
      
      const response = await adminFetch(
        `/api/redemptions/validate/${encodeURIComponent(code.trim())}?${params}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setValidation(data);
        if (data.valid) {
          setShowRedeemForm(true);
        }
      }
    } catch (_err) {
      setValidation({
        valid: false,
        message: 'Failed to validate code',
        promo: null
      });
    } finally {
      setValidating(false);
    }
  };

  // Redeem coupon
  const handleRedeem = async () => {
    if (!validation?.promo) return;
    
    if (!customerName.trim() && !customerEmail.trim() && !customerPhone.trim()) {
      alert('Please enter at least one customer identifier (name, email, or phone)');
      return;
    }
    
    setRedeeming(true);
    
    try {
      const response = await adminFetch('/api/redemptions/redeem', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          promo_code: code.trim(),
          customer_name: customerName.trim() || null,
          customer_email: customerEmail.trim() || null,
          customer_phone: customerPhone.trim() || null,
          redeemed_by: user?.email?.split('@')[0] || 'Tammy', // Uses authenticated user's name
          verification_method: 'in_store',
          notes: notes.trim() || null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccessMessage(`✅ ${validation.promo.title} redeemed successfully!`);
        // Reset form
        setCode('');
        setValidation(null);
        setShowRedeemForm(false);
        setCustomerName('');
        setCustomerEmail('');
        setCustomerPhone('');
        setNotes('');
        // Refresh data
        fetchHistory();
        fetchStats();
        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        alert(`❌ ${data.message || 'Failed to redeem coupon'}\n\nTip: Try refreshing the page and try again.`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to redeem coupon';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    } finally {
      setRedeeming(false);
    }
  };

  // Void redemption
  const voidRedemption = async (id: string, title: string) => {
    if (!confirm(`Void redemption for "${title}"? This will restore the coupon usage.`)) return;
    
    const reason = prompt('Reason for voiding (optional):');
    
    try {
      const response = await adminFetch(`/api/redemptions/${id}/void`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          voided_by: 'Tammy',
          reason: reason || 'Voided by admin'
        })
      });
      
      if (response.ok) {
        fetchHistory();
        fetchStats();
      } else {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error || 'Failed to void redemption';
        alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to void redemption';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coupon Redemptions</h1>
          <p className="text-gray-500 mt-1">
            Verify and track coupon usage
          </p>
        </div>
        <button
          onClick={() => { fetchHistory(); fetchStats(); }}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <HiRefresh className="w-5 h-5" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="text-3xl font-bold text-purple-600">{stats.today_redemptions}</div>
            <div className="text-gray-500 text-sm">Redeemed Today</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-3xl font-bold text-blue-600">{stats.week_redemptions}</div>
            <div className="text-gray-500 text-sm">This Week</div>
          </div>
          <div className="bg-white rounded-xl border p-4">
            <div className="text-3xl font-bold text-gray-700">{stats.total_redemptions}</div>
            <div className="text-gray-500 text-sm">Total All Time</div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700 flex items-center gap-3">
          <HiCheck className="w-6 h-6" />
          <span className="font-medium">{successMessage}</span>
        </div>
      )}

      {/* Validation Section */}
      <div className="bg-white rounded-xl border p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <HiClipboardCheck className="w-5 h-5 text-purple-600" />
          Verify Coupon Code
        </h2>
        
        <div className="flex gap-3">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && validateCode()}
            placeholder="Enter coupon code (e.g., KUMON10)"
            className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 font-mono text-lg uppercase"
          />
          <button
            onClick={validateCode}
            disabled={validating || !code.trim()}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 
                       disabled:opacity-50 flex items-center gap-2 font-medium"
          >
            {validating ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <HiSearch className="w-5 h-5" />
                Verify
              </>
            )}
          </button>
        </div>

        {/* Validation Result */}
        {validation && (
          <div className={`mt-4 p-4 rounded-lg ${
            validation.valid 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start gap-3">
              {validation.valid ? (
                <HiCheck className="w-6 h-6 text-green-600 mt-0.5" />
              ) : (
                <HiX className="w-6 h-6 text-red-600 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`font-medium ${validation.valid ? 'text-green-800' : 'text-red-800'}`}>
                  {validation.message}
                </p>
                
                {validation.promo && (
                  <div className="mt-3 space-y-2">
                    <h3 className="font-semibold text-gray-900">{validation.promo.title}</h3>
                    {validation.promo.description && (
                      <p className="text-gray-600 text-sm">{validation.promo.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {validation.promo.discount_type === 'percentage' && validation.promo.discount_value && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm font-medium">
                          {validation.promo.discount_value}% OFF
                        </span>
                      )}
                      {validation.promo.discount_type === 'fixed' && validation.promo.discount_value && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm font-medium">
                          ${validation.promo.discount_value} OFF
                        </span>
                      )}
                      {validation.promo.free_item && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
                          🎁 FREE: {validation.promo.free_item}
                        </span>
                      )}
                      {validation.promo.target_audience && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                          For: {validation.promo.target_audience}
                        </span>
                      )}
                      {validation.promo.uses_remaining !== null && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                          {validation.promo.uses_remaining} uses remaining
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Redemption Form */}
        {showRedeemForm && validation?.valid && (
          <div className="mt-6 pt-6 border-t">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <HiUser className="w-5 h-5" />
              Customer Information
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              Enter at least one identifier to track this redemption and prevent fraud.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="john@email.com"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="705-555-1234"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes (optional)
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special notes about this redemption"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <div className="mt-6 flex gap-3">
              <button
                onClick={handleRedeem}
                disabled={redeeming}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 
                           disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                {redeeming ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <HiCheck className="w-5 h-5" />
                    Confirm Redemption
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowRedeemForm(false);
                  setValidation(null);
                  setCode('');
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Recent Redemptions */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <HiClock className="w-5 h-5 text-gray-500" />
            Recent Redemptions
          </h2>
          <span className="text-gray-500 text-sm">{redemptions.length} shown</span>
        </div>
        
        {loadingHistory ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-gray-500 mt-4">Loading...</p>
          </div>
        ) : redemptions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-4">📋</div>
            <p className="text-gray-500">No redemptions yet</p>
          </div>
        ) : (
          <div className="divide-y">
            {redemptions.map(redemption => (
              <div 
                key={redemption.id} 
                className={`p-4 ${redemption.status === 'voided' ? 'bg-gray-50 opacity-60' : ''}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-sm">
                        {redemption.promo_code}
                      </span>
                      <span className="font-medium text-gray-900">
                        {redemption.promo?.title}
                      </span>
                      {redemption.status === 'voided' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">
                          VOIDED
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 space-y-0.5">
                      {redemption.customer_name && (
                        <p>👤 {redemption.customer_name}</p>
                      )}
                      {redemption.customer_email && (
                        <p>✉️ {redemption.customer_email}</p>
                      )}
                      {redemption.customer_phone && (
                        <p>📞 {redemption.customer_phone}</p>
                      )}
                      <p>🕐 {formatDate(redemption.redeemed_at)} • by {redemption.redeemed_by}</p>
                    </div>
                    {redemption.promo?.free_item && (
                      <p className="text-green-600 text-sm mt-1">
                        🎁 {redemption.promo.free_item}
                      </p>
                    )}
                  </div>
                  {redemption.status !== 'voided' && (
                    <button
                      onClick={() => voidRedemption(redemption.id, redemption.promo?.title || '')}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      title="Void this redemption"
                    >
                      <HiBan className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fraud Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <HiExclamation className="w-6 h-6 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-800">Fraud Prevention Tips</h3>
            <ul className="mt-2 text-sm text-yellow-700 space-y-1">
              <li>• Always verify the customer is who they claim to be</li>
              <li>• Ask for ID if the promo is targeted (e.g., Kumon families)</li>
              <li>• Check if the email/phone has been used before</li>
              <li>• Watch for repeat attempts with slight variations</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

